/**
 * Cognito sign-in for the Pro Hub on the cell (move map M28). No SDK: the four calls the hub needs are plain JSON
 * POSTs to Cognito's own endpoint (InitiateAuth / RespondToAuthChallenge / GetTokensFromRefreshToken-by-InitiateAuth /
 * RevokeToken), which Cognito serves to browsers (CORS).
 *
 * Sign-in is choice-based (USER_AUTH): the pool allows PASSWORD and WEB_AUTHN first factors (TeneanIdentity). Email
 * codes — the hub's magic link and 8-digit code today — arrive with the email rail (M18); until then the AWS staging
 * build signs in with a password (synthetic test people and the QA pro only; nobody real is in the pool).
 *
 * The session lives in localStorage under its own key (never Supabase's), refreshes itself before the ID token
 * expires, and sign-out is LOCAL to this device (revoking only this device's refresh token) — the same rule as
 * auth.ts signOut (decision d0aaa17a).
 */
import { COGNITO } from './config';

export interface CellSession {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /** epoch seconds the ID token expires */
  expiresAt: number;
  /** from the ID token: the person's Tenean user id (the old auth.users id) and email */
  userId: string;
  email: string | null;
}

export const STORAGE_KEY = 'tenean-cell-session';
const listeners = new Set<(s: CellSession | null) => void>();
let current: CellSession | null = load();

function load(): CellSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CellSession) : null;
  } catch { return null; }
}
function save(s: CellSession | null) {
  current = s;
  try { if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); else localStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
  listeners.forEach((fn) => fn(s));
}

export function claimsOf(jwt: string): Record<string, unknown> {
  const b = jwt.split('.')[1] ?? '';
  const json = decodeURIComponent(atob(b.replace(/-/g, '+').replace(/_/g, '/')).split('').map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''));
  return JSON.parse(json) as Record<string, unknown>;
}

function fromTokens(t: { IdToken: string; AccessToken: string; RefreshToken?: string }, keepRefresh?: string): CellSession {
  const c = claimsOf(t.IdToken);
  const userId = String(c.app_user_id ?? c['custom:app_user_id'] ?? '');
  if (!/^[0-9a-f-]{36}$/i.test(userId)) throw new Error('This account is not set up for Tenean yet.');
  return {
    idToken: t.IdToken, accessToken: t.AccessToken, refreshToken: t.RefreshToken ?? keepRefresh ?? '',
    expiresAt: Number(c.exp ?? 0), userId: userId.toLowerCase(), email: typeof c.email === 'string' ? c.email : null,
  };
}

async function cognito<T>(target: string, body: Record<string, unknown>): Promise<T> {
  const r = await fetch(`https://cognito-idp.${COGNITO.region}.amazonaws.com/`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-amz-json-1.1', 'x-amz-target': `AWSCognitoIdentityProviderService.${target}` },
    body: JSON.stringify(body),
  });
  const j = (await r.json().catch(() => ({}))) as T & { message?: string; __type?: string };
  if (!r.ok) throw new Error(friendly(j.__type, j.message));
  return j;
}

function friendly(type?: string, message?: string): string {
  if (/NotAuthorized|UserNotFound/.test(type ?? '')) return 'That email and password did not match.';
  if (/TooManyRequests|LimitExceeded/.test(type ?? '')) return 'Too many tries — wait a minute and try again.';
  return message || 'Sign-in failed. Try again.';
}

type Auth = { AuthenticationResult?: { IdToken: string; AccessToken: string; RefreshToken?: string }; ChallengeName?: string; Session?: string };

/** Email + password through USER_AUTH (PASSWORD first factor). */
export async function signInWithPassword(email: string, password: string): Promise<CellSession> {
  const username = email.trim().toLowerCase();
  let r = await cognito<Auth>('InitiateAuth', {
    // With PREFERRED_CHALLENGE PASSWORD, Cognito wants the password in this first call and answers with tokens
    // ("Missing required parameter PASSWORD" without it — seen on the staging pool 2026-10-22). The challenge branches
    // below stay for a pool that answers with SELECT_CHALLENGE or PASSWORD instead.
    AuthFlow: 'USER_AUTH', ClientId: COGNITO.clientId, AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: 'PASSWORD', PASSWORD: password },
  });
  if (!r.AuthenticationResult && r.ChallengeName === 'SELECT_CHALLENGE') {
    r = await cognito<Auth>('RespondToAuthChallenge', {
      ClientId: COGNITO.clientId, ChallengeName: 'SELECT_CHALLENGE', Session: r.Session,
      ChallengeResponses: { USERNAME: username, ANSWER: 'PASSWORD', PASSWORD: password },
    });
  } else if (!r.AuthenticationResult && r.ChallengeName === 'PASSWORD') {
    r = await cognito<Auth>('RespondToAuthChallenge', {
      ClientId: COGNITO.clientId, ChallengeName: 'PASSWORD', Session: r.Session, ChallengeResponses: { USERNAME: username, PASSWORD: password },
    });
  }
  if (!r.AuthenticationResult) throw new Error(r.ChallengeName ? `This account needs a step the AWS staging hub does not offer yet (${r.ChallengeName}).` : 'Sign-in failed.');
  const s = fromTokens(r.AuthenticationResult);
  save(s);
  return s;
}

let refreshing: Promise<CellSession | null> | null = null;
/** The session, refreshed when the ID token has under two minutes left. Null = signed out. */
export async function getCellSession(): Promise<CellSession | null> {
  const s = current;
  if (!s) return null;
  if (s.expiresAt - Date.now() / 1000 > 120) return s;
  if (!s.refreshToken) { save(null); return null; }
  refreshing ??= (async () => {
    try {
      const r = await cognito<Auth>('InitiateAuth', { AuthFlow: 'REFRESH_TOKEN_AUTH', ClientId: COGNITO.clientId, AuthParameters: { REFRESH_TOKEN: s.refreshToken } });
      const n = r.AuthenticationResult ? fromTokens(r.AuthenticationResult, s.refreshToken) : null;
      save(n);
      return n;
    } catch { save(null); return null; } finally { refreshing = null; }
  })();
  return refreshing;
}

export const peekCellSession = (): CellSession | null => current;

export function onCellSession(fn: (s: CellSession | null) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Sign out of THIS device only: revoke this device's refresh token, forget the session. */
export async function signOutCell(): Promise<void> {
  const s = current;
  save(null);
  if (s?.refreshToken) await cognito('RevokeToken', { ClientId: COGNITO.clientId, Token: s.refreshToken }).catch(() => undefined);
}
